import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NavRoutes } from '../../constants/nav.routes';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { CommonModule } from '@angular/common';
import { FlexModule } from '@angular/flex-layout/flex';
import { MatCardModule } from '@angular/material/card';
import { ValidationConstants } from '../../constants/validation.constants';
import { BaseComponent } from '../../../shared/components/base/base.component';
import { KioskService } from 'src/openapi-gen/services';
import { KioskPinDto, KioskRole } from 'src/openapi-gen/models';
import { ProgressBarComponent } from 'src/app/shared/components/progress-bar/progress-bar.component';
import { ErrorResponseHandler } from '../../../shared/services/error-response-handler';
import { KioskUserService } from 'src/app/shared/services/kiosk-user.service';
import { AuthService } from 'src/app/auth/auth.service';
import { TransactionRoutingService } from 'src/app/shared/services/transaction-routing.service';
import { TransactionPacket } from 'src/app/shared/models/transaction-packet';
import { TransactionTypes } from '../../constants/transaction.types';
import { KioskScanPageComponent } from 'src/app/pages/kiosk-scan-page/kiosk-scan-page.component';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-pin-dialog',
  templateUrl: './pin-dialog.component.html',
  styleUrls: ['./pin-dialog.component.scss'],
  standalone: true,
  imports: 
  [
    CommonModule,
    FlexModule, 
    ExtendedModule, 
    FormsModule, 
    ReactiveFormsModule, 

    MatCardModule, 
    MatFormFieldModule, 
    MatInputModule, 
    MatButtonModule, 
    MatIconModule,
    MatDialogModule,

    ProgressBarComponent,
  ]
})
export class PinDialogComponent extends BaseComponent implements OnInit {
  @ViewChild(ProgressBarComponent, { static: true }) progressBar: ProgressBarComponent;
  
  readonly PIN_MAX_LENGTH: number = ValidationConstants.PIN_LENGTH;
  
  loading: boolean = false;

  form: FormGroup;
  isLogoutDialog: boolean = false;
  errorMessage: string = "";

  form_validation_messages: any = {
    'pin': [
      { type: 'required', message: 'Please enter your PIN' },
      { type: 'pattern', message: 'A PIN must be numerical' },
      { type: 'minlength', message: 'A PIN is 5 digits' },
    ],
  }

  constructor(
    private router: Router, 
    public dialogRef: MatDialogRef<PinDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private kioskService: KioskService,
    private kioskUserService: KioskUserService,
    private transactionRoutingService: TransactionRoutingService,
    private formBuider: FormBuilder,
    private errorResponseHandler: ErrorResponseHandler
  ) { 
    super();
    this.isLogoutDialog = dialogData.isLoggingOut;
  }

  ngOnInit() {
    this.form = this.formBuider.group({
      pin: new FormControl(null, [
        Validators.required, 
        Validators.pattern("[0-9]*"), 
        Validators.minLength(this.PIN_MAX_LENGTH), 
        Validators.maxLength(this.PIN_MAX_LENGTH)
      ]),
    });
  }

  close() {
    this.dialogRef.close(false);
  }

  check() {
    this.errorMessage = undefined;

    const pin = this.form.controls.pin.value;
    this.loadingOn();

    if(this.isLogoutDialog) {
      this.logoutKiosk(pin);
      return;
    }

    this.signInAffilition(pin);
  }

  invalidPin() {
    return this.form.valid == false || this.form.controls.pin.value.length !== this.PIN_MAX_LENGTH;
  }

  private signInAffilition(pin: number) {
    this.kioskService
      .apiKioskSearchByPinPost({body: { pin: pin } as KioskPinDto })
      .subscribe({
        next: (data) => {
          this.kioskUserService.saveAffiliatedUser(data);

          if(data.role === KioskRole.Agency) {
            this.outGoingForAgency();
          }
          else {
            this.router.navigate([NavRoutes.Kiosk, NavRoutes.KioskOptions]);  
          }
          
          this.dialogRef.close(true);
        },
        error: (err) => {
          this.form.reset();
          this.loadingOff();
          this.errorMessage = this.errorResponseHandler.handle_kiosk_pin_login(err.status);
        }
      });
  }

  private logoutKiosk(pin: number) {
    this.kioskService
      .apiKioskLogoutAccountPost({ body: { pin: pin } as KioskPinDto })
      .subscribe({
          next: _ => {
            KioskScanPageComponent.hasInfoDialogBeenShown = false;
            AuthService.SetUserLoggedOut();
            this.router.navigate([NavRoutes.Login], { replaceUrl: true });
            this.dialogRef.close(true);
          },
          error: (err) => {
            this.form.reset();
            this.loadingOff();
            this.errorMessage = this.errorResponseHandler.handle_kiosk_logout(err.status, this.dialogRef);
          }
      });
  }

  private outGoingForAgency() {
    let data = {
      title: 'OUTGOING PRODUCT WEIGHT',
      type: TransactionTypes.Outgoing,
      kiosk: true,
    } as TransactionPacket;

    this.transactionRoutingService.updatePacket(data);
    this.router.navigate([NavRoutes.Kiosk, NavRoutes.Transaction]);
  }

  private loadingOn(): void {
    this.loading = true;
    this.progressBar.show();
  }

  private loadingOff(): void {
    this.loading = false;
    this.progressBar.hide();
  }
}